import React, { useEffect, useRef, useState } from 'react';
import * as _ from 'lodash';
import { ContractType, getContractTypeName } from 'domain/contract';
import { Button, Container, Form, Modal, Row } from 'react-bootstrap';
import ContractTypeSetting from './contractTypeSetting';
import TypedSelect from 'common/ui/TypedSelect';
import { AllDeliveryMethods, DeliveryMethod, DeliveryMethodDetails, getDeliveryMethodText } from 'domain/deliveryMethod';
import loggingService from 'common/logging/loggingService';
import notificationService from 'common/notificationService';
import AsyncButton from 'common/ui/AsyncButton';
import FormValidation, { FormValidationRefProps } from 'common/ui/FormValidation';

export interface ContractTypeSettingsProps {
    deliveryMethods: DeliveryMethodDetails[];
    contractTypeSettings: ContractTypeSetting[];
    onChange(updatedSetting: ContractTypeSetting): void;
    enabledContractTypes: ContractType[];
    onContractTypeChanged(contractType: ContractType, isEnabled: boolean): void;
    onDeliveryMethodDetailsChanged(deliveryMethodDetails: DeliveryMethodDetails): Promise<any>;
}

const contractTypes = [ContractType.Cash, ContractType.Hta, ContractType.Basis, ContractType.DelayedPrice, ContractType.CashMinPrice, ContractType.HtaMinPrice];

const ContractTypeSettings: React.FC<ContractTypeSettingsProps> = props => {
    const [selectedContractType, setSelectedContractType] = useState<ContractType>(ContractType.Cash);
    const [settings, setSettings] = useState<ContractTypeSetting[]>([]);
    const [selectedDeliveryMethodDetails, setSelectedDeliveryMethodDetails] = useState<DeliveryMethodDetails>(null);

    const formRef = useRef<FormValidationRefProps>(null);

    useEffect(() => {
        setSettings(contractTypes.flatMap(ct => AllDeliveryMethods.map(dm => {
            return props.contractTypeSettings.find(m => m.contractType === ct && m.deliveryMethod === dm)
                ?? {
                contractType: ct,
                deliveryMethod: dm,
                isDefaultDeliveryMethod: false,
                canAddContract: true,
                canEditContract: true,
                canEditContractQuantity: true
            };
        })));
    }, [props.contractTypeSettings]);

    function handleChange(newSetting: ContractTypeSetting) {
        if (!newSetting.canAddContract) {
            newSetting.canEditContract = newSetting.canEditContractQuantity = false;
        }
        else if (!newSetting.canEditContract) {
            newSetting.canEditContractQuantity = false;
        }


        props.onChange(newSetting);
    }

    function handleDefaultChange(newSetting: ContractTypeSetting) {
        if(newSetting.isDefaultDeliveryMethod) {
            const oldSetting = settings.find(m => m.contractType === selectedContractType && m.isDefaultDeliveryMethod === true);
            if(oldSetting != null && oldSetting.deliveryMethod !== newSetting.deliveryMethod) {
                oldSetting.isDefaultDeliveryMethod = false;
                props.onChange(oldSetting);
            }
        }

        props.onChange(newSetting);
    }

    function onDeliveryMethodSelected(deliveryMethod: DeliveryMethod): void {
        setSelectedDeliveryMethodDetails({ ...props.deliveryMethods.find(dmd => dmd.deliveryMethod === deliveryMethod) });
    }

    async function saveDeliveryMethodDetails() {
        try {
            await props.onDeliveryMethodDetailsChanged(selectedDeliveryMethodDetails);
            setSelectedDeliveryMethodDetails(null);
        }
        catch (err) {
            loggingService.error("Error updating delivery method", err as Error);
            notificationService.error("Error updating delivery method");
        }
    }

    function getDefaultDeliveryTerms(deliveryMethod: DeliveryMethod): string {
        return props.deliveryMethods?.find(dmd => dmd.deliveryMethod === deliveryMethod)?.defaultDeliveryTerms;
    }

    const isContractTypeEnabled = props.enabledContractTypes.includes(selectedContractType);

    return <>
        <div className="row">
            <div className="col-2">
                <TypedSelect
                    options={contractTypes}
                    value={selectedContractType}
                    valueGetter={m => m}
                    textGetter={getContractTypeName}
                    onValueSelected={setSelectedContractType} />
            </div>
            <div className="col-2">
                <label>Enabled</label>
                <Form.Check type="switch" id="enable-contract-type"
                    className="ml-2"
                    inline
                    checked={isContractTypeEnabled}
                    onChange={ev => props.onContractTypeChanged(selectedContractType, ev.target.checked)} />
            </div>
        </div>
        <div className="settings-box">
            <div className="row">
                <div className="col-2"><strong>Delivery Method</strong></div>
                <div className="col-2"><strong>Default Delivery Terms</strong></div>
                <div className="col-1"><strong>Default</strong></div>
                <div className="col-1"><strong>Can Add</strong></div>
                <div className="col-1"><strong>Can Edit</strong></div>
                <div className="col-1"><strong>Can Edit Quantity</strong></div>
            </div>
            {settings.filter(m => m.contractType === selectedContractType)
                .map(setting => <div className="row" key={setting.deliveryMethod}>
                    <div className="col-2">
                        <button className="link-button"  onClick={() => onDeliveryMethodSelected(setting.deliveryMethod)}>
                            {getDeliveryMethodText(setting.deliveryMethod, props.deliveryMethods)}
                        </button>
                    </div>
                    <div className="col-2">
                        {getDefaultDeliveryTerms(setting.deliveryMethod)}
                    </div>
                    <div className="col-1">
                        <Form.Check checked={setting.isDefaultDeliveryMethod} disabled={!setting.canAddContract || !isContractTypeEnabled}
                            onChange={ev => handleDefaultChange({...setting, isDefaultDeliveryMethod: ev.target.checked})} />
                    </div>
                    <div className="col-1">
                        <Form.Check checked={setting.canAddContract}
                            disabled={!isContractTypeEnabled}
                            onChange={ev => handleChange({...setting, canAddContract: ev.target.checked})} />
                    </div>
                    <div className="col-1">
                        <Form.Check checked={setting.canEditContract}
                            disabled={!setting.canAddContract || !isContractTypeEnabled}
                            onChange={ev => handleChange({...setting, canEditContract: ev.target.checked})} />
                    </div>
                    <div className="col-1">
                        <Form.Check checked={setting.canEditContractQuantity} 
                            disabled={!setting.canEditContract || !isContractTypeEnabled}
                            onChange={ev => handleChange({...setting, canEditContractQuantity: ev.target.checked})}/>
                    </div>
                </div>
            )}
        </div>
        {selectedDeliveryMethodDetails &&
            <Modal show onHide={() => setSelectedDeliveryMethodDetails(null)} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Delivery Method</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Container>
                        <FormValidation ref={formRef}>
                            <Form.Group as={Row}>
                                <Form.Label>Name</Form.Label>
                                <div className="ml-2">{getDeliveryMethodText(selectedDeliveryMethodDetails.deliveryMethod)}</div>
                            </Form.Group>
                            <Form.Group as={Row}>
                                <Form.Label className="required">Display Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="displayName"
                                    value={selectedDeliveryMethodDetails.name ?? getDeliveryMethodText(selectedDeliveryMethodDetails.deliveryMethod)}
                                    onChange={(ev: any) => setSelectedDeliveryMethodDetails(() => ({ ...selectedDeliveryMethodDetails, name: ev.target.value }))}
                                    required />
                                <Form.Control.Feedback type="invalid">
                                    Please provide a valid display name
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group as={Row}>
                                <Form.Label className="required">Default Delivery Terms</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="defaultDeliveryTerms"
                                    value={selectedDeliveryMethodDetails.defaultDeliveryTerms}
                                    onChange={(ev: any) => setSelectedDeliveryMethodDetails(() => ({ ...selectedDeliveryMethodDetails, defaultDeliveryTerms: ev.target.value }))}
                                    required />
                                <Form.Control.Feedback type="invalid">
                                    Please provide a valid default delivery terms
                                </Form.Control.Feedback>
                            </Form.Group>
                        </FormValidation>
                    </Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSelectedDeliveryMethodDetails(null)}>
                        Cancel
                    </Button>
                    <AsyncButton variant="primary" onClickAsync={saveDeliveryMethodDetails}>
                        Save
                    </AsyncButton>
                </Modal.Footer>
            </Modal>
        }
    </>
}

export default ContractTypeSettings;